'use client';

import React from 'react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { cn } from '@/lib/utils';
import { Cursor } from '@/components/animations/SmartCursor';
import { withRef } from '@/lib/with-ref';
import Button from '../Root';
import LinkButton from '../Link';
import type { IconButtonElement, IconButtonProps } from './types';

const ButtonIcon = withRef<IconButtonElement, IconButtonProps>(
  (
    {
      label = '',
      variant = 'default',
      icon,
      href,
      className = '',
      children,
      ...props
    },
    ref,
  ) => {
    const buttonClasses = cn('gap-2');

    const renderIcon = () => {
      if (!icon) {
        return null;
      }

      if (typeof icon === 'string') {
        return <DynamicIcon name={icon as IconName} />;
      }

      if (React.isValidElement(icon)) {
        return icon;
      }

      if (typeof icon === 'function') {
        const IconComp = icon;

        return <IconComp />;
      }

      return null;
    };

    if (href) {
      return (
        <LinkButton
          layout={true}
          variant={variant}
          href={href}
          className={cn('relative p-2', buttonClasses, className)}
          {...props}
        >
          <Cursor type="grow" />

          {renderIcon()}
          {label}
          {children}
        </LinkButton>
      );
    }

    return (
      <Button
        variant={variant}
        className={cn('relative p-2', buttonClasses, className)}
        {...props}
      >
        <Cursor type="grow" />

        {renderIcon()}
        {label}
        {children}
      </Button>
    );
  },
);

export default React.memo(ButtonIcon);
